/**
 * ‹ › in full screen: which chart is next, in Z order.
 *
 * Read off the layout's grid rather than the `sections` array, because array
 * order is the order sections were *created*, and a rearrangement moves cells
 * without reordering the array — so the keys would jump around the screen in
 * an order nobody can see. Row first, then column: left to right, then the row
 * below, which is how the layout reads. A chart spanning several cells counts
 * from its top-left one. The screener is not a chart and is skipped.
 *
 * Pure, so the order is tested here rather than in the one place this app
 * cannot test.
 */

import type { Layout } from "@qufolio/model";
import { chartGroupConfig } from "./section-config";

/** The layout's chart section ids, in Z order. */
export function chartCycleOrder(layout: Layout): string[] {
  return layout.sections
    .filter((s) => chartGroupConfig(s) !== undefined)
    .map((s, i) => ({ id: s.id, row: s.row, column: s.column, i }))
    .sort((a, b) => a.row - b.row || a.column - b.column || a.i - b.i)
    .map((s) => s.id);
}

/**
 * The chart `step` places from `current`, wrapping at both ends. `null` when
 * there is nothing to move to — one chart, or `current` is not a chart here.
 */
export function cycleChart(layout: Layout, current: string, step: 1 | -1): string | null {
  const order = chartCycleOrder(layout);
  const at = order.indexOf(current);
  if (at < 0 || order.length < 2) return null;
  return order[(at + step + order.length) % order.length]!;
}
